import * as React from 'react'

import { cn } from '@/registry/reapit/lib/utils'

type ChipSelectSize = 'small' | 'medium' | 'large'

type ChipSelectOption = {
  value: string
  label: string
  icon?: React.ReactNode
  disabled?: boolean
}

type ChipSelectProps = {
  options: ChipSelectOption[]
  value?: string | string[]
  onValueChange?: (value: string | string[]) => void
  multiple?: boolean
  size?: ChipSelectSize
  disabled?: boolean
  error?: boolean
  className?: string
}

function ChipSelect({
  options,
  value,
  onValueChange,
  multiple = false,
  size = 'medium',
  disabled = false,
  error = false,
  className,
}: ChipSelectProps) {
  const selectedValues = React.useMemo(() => {
    if (!value) return new Set<string>()
    return new Set(Array.isArray(value) ? value : [value])
  }, [value])

  function handleSelect(optionValue: string) {
    if (disabled) return
    if (multiple) {
      const next = new Set(selectedValues)
      if (next.has(optionValue)) {
        next.delete(optionValue)
      } else {
        next.add(optionValue)
      }
      onValueChange?.(Array.from(next))
    } else {
      onValueChange?.(selectedValues.has(optionValue) ? '' : optionValue)
    }
  }

  return (
    // DS ChipSelect: gap-2 (8px) between chips, rounded-full pill style
    // Sizes: small px-2.5 py-0.5 text-xs; medium px-3 py-1 text-sm; large px-4 py-1.5 text-sm
    // Selected: border-primary bg-fill-action-lightest (purple-050) text-primary
    // Unselected: border-input bg-background text-foreground; hover: bg-accent
    // Error (unselected): border-destructive
    <div
      data-slot="chip-select"
      data-size={size}
      data-error={error || undefined}
      className={cn('flex flex-wrap gap-2', disabled && 'pointer-events-none opacity-50', className)}
    >
      {options.map((option) => {
        const isSelected = selectedValues.has(option.value)
        return (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={isSelected}
            disabled={disabled || option.disabled}
            onClick={() => handleSelect(option.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors outline-none',
              'focus-visible:ring-[3px] focus-visible:ring-ring/50',
              'disabled:cursor-not-allowed disabled:opacity-50',
              // Sizes
              size === 'small' && 'px-2.5 py-0.5 text-xs',
              size === 'medium' && 'px-3 py-1 text-sm',
              size === 'large' && 'px-4 py-1.5 text-sm',
              // States — DS: selected uses fill-action-lightest (purple-050) tint
              isSelected
                ? 'border-primary bg-fill-action-lightest text-primary hover:bg-fill-action-light'
                : 'border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
              error && !isSelected && 'border-destructive',
            )}
          >
            {option.icon && (
              // DS: inline chip icons at size-4 (icon-sm, 16px) for all sizes except small (size-3)
              <span
                className={cn('shrink-0', size === 'small' ? 'size-3 [&>svg]:size-full' : 'size-4 [&>svg]:size-full')}
              >
                {option.icon}
              </span>
            )}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export { ChipSelect }
export type { ChipSelectProps, ChipSelectOption, ChipSelectSize }
